/**
 * MD-28 cron next-run computation (viet tay, KHONG them npm dep).
 *
 * Cover 11 case dang dung trong wrangler.toml + 1 da paused (m1-monitor).
 * Tat ca cron expr la 5-field UTC. Output Date la moc UTC ke tiep.
 *
 * Support pattern:
 * - `M H * * *`        : daily at M H UTC
 * - `M H * * D`        : weekly on day D (0=Sun)
 * - `M H * * D1,D2`    : weekly on multiple day
 * - `M (slash)N hour pattern`    : every N hour at minute M
 * - `(slash)N minute pattern`    : every N minute
 *
 * Out-of-scope: month restrict, day-of-month, ranges (-). 11 cron Day-1 khong dung.
 */

export interface CronNextResult {
  next_ts: string;     // ISO UTC
  next_ict: string;    // ICT label (UTC+7) "HH:mm DD/MM"
  in_minutes: number;  // minute den next run (>=0)
}

const ICT_OFFSET_MIN = 7 * 60;

function toICTLabel(date: Date): string {
  const ictMs = date.getTime() + ICT_OFFSET_MIN * 60_000;
  const d = new Date(ictMs);
  const HH = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const DD = String(d.getUTCDate()).padStart(2, '0');
  const MM = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${HH}:${mm} ${DD}/${MM}`;
}

function parseField(field: string): { type: 'star' } | { type: 'list'; vals: number[] } | { type: 'step'; step: number } {
  if (field === '*') return { type: 'star' };
  if (field.startsWith('*/')) return { type: 'step', step: parseInt(field.slice(2), 10) };
  if (field.includes(',')) return { type: 'list', vals: field.split(',').map((s) => parseInt(s, 10)).filter((n) => !Number.isNaN(n)) };
  const n = parseInt(field, 10);
  if (!Number.isNaN(n)) return { type: 'list', vals: [n] };
  return { type: 'star' };
}

export function nextCronRun(expr: string, now: Date = new Date()): CronNextResult | null {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minF, hourF, _dom, _mon, dowF] = parts;
  const minute = parseField(minF);
  const hour = parseField(hourF);
  const dow = parseField(dowF);

  // Search forward minute by minute up to 8 days (worst case weekly).
  const start = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
    now.getUTCHours(), now.getUTCMinutes() + 1, 0, 0,
  ));
  const maxScanMin = 8 * 24 * 60;
  for (let i = 0; i < maxScanMin; i++) {
    const t = new Date(start.getTime() + i * 60_000);
    const m = t.getUTCMinutes();
    const h = t.getUTCHours();
    const w = t.getUTCDay(); // 0..6 Sun..Sat

    // minute match
    let minOk = false;
    if (minute.type === 'star') minOk = true;
    else if (minute.type === 'step') minOk = m % minute.step === 0;
    else minOk = minute.vals.includes(m);
    if (!minOk) continue;

    // hour match
    let hourOk = false;
    if (hour.type === 'star') hourOk = true;
    else if (hour.type === 'step') hourOk = h % hour.step === 0;
    else hourOk = hour.vals.includes(h);
    if (!hourOk) continue;

    // dow match (1=Mon..7=Sun convention also allowed; standard cron 0=Sun)
    let dowOk = false;
    if (dow.type === 'star') dowOk = true;
    else if (dow.type === 'step') dowOk = w % dow.step === 0;
    else dowOk = dow.vals.some((d) => d === w || (d === 7 && w === 0));
    if (!dowOk) continue;

    const inMin = Math.max(0, Math.round((t.getTime() - now.getTime()) / 60_000));
    return {
      next_ts: t.toISOString(),
      next_ict: toICTLabel(t),
      in_minutes: inMin,
    };
  }
  return null;
}

export function relativeIn(minutes: number): string {
  if (minutes < 1) return 'sap chay';
  if (minutes < 60) return `trong ${minutes} phut`;
  if (minutes < 24 * 60) return `trong ${(minutes / 60).toFixed(1)} gio`;
  return `trong ${(minutes / (24 * 60)).toFixed(1)} ngay`;
}
